import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const GUIDES = {
  dashboard: {
    title: 'Dashboard',
    icon: '📊',
    steps: [
      { title: 'Overview', desc: 'Your dashboard shows key metrics including message volume, active conversations, and response times.' },
      { title: 'Send Limits', desc: 'The send limits widget shows your current usage against 10DLC carrier limits (per minute, hour, day, month).' },
      { title: 'AI Token Usage', desc: 'If AI Analytics is enabled, you\'ll see token consumption metrics here.' },
      { title: 'Recent Conversations', desc: 'Quick access to your most recent conversations without navigating to Messages.' }
    ]
  },
  messages: {
    title: 'Messaging',
    icon: '💬',
    steps: [
      { title: 'Conversation List', desc: 'Your conversations are listed on the left. Unread counts show as badges. Click a conversation to open it.' },
      { title: 'Sending Messages', desc: 'Type your message at the bottom and press Enter or click Send. You can attach images and media.' },
      { title: 'AI Analysis', desc: 'The purple bar above messages shows AI sentiment analysis. Click "Details" for full insights including satisfaction scores and action items.' },
      { title: 'Conversation Actions', desc: 'Use the menu icon (⋮) on each conversation for options like Archive, Assign, Tag, and more.' },
      { title: 'New Message', desc: 'Click the + button to start a new conversation. Enter a phone number or select from contacts.' },
      { title: 'Group Messages', desc: 'If enabled, you can send messages to 2-10 recipients at once using the group message button.' }
    ]
  },
  contacts: {
    title: 'Contacts',
    icon: '👥',
    steps: [
      { title: 'Contact List', desc: 'View and manage all your contacts. Search, filter by company or opt-out status.' },
      { title: 'Add Contact', desc: 'Click "Add Contact" to create a new contact with name, phone number, and custom fields.' },
      { title: 'Import/Export', desc: 'Bulk import contacts via CSV upload, or export your contact list.' },
      { title: 'Opt-Out List', desc: 'View contacts who have opted out. You can opt them back in if they request it.' },
      { title: 'Allow List', desc: 'Manage your allowed contacts list for compliance.' }
    ]
  },
  templates: {
    title: 'Templates',
    icon: '📝',
    steps: [
      { title: 'Message Templates', desc: 'Create reusable message templates to save time on common responses.' },
      { title: 'Categories', desc: 'Organize templates by category for quick access during conversations.' },
      { title: 'Variables', desc: 'Use variables like {{first_name}} and {{company}} that auto-fill with contact information.' },
      { title: 'Quick Insert', desc: 'In the message composer, type / to search and insert templates inline.' }
    ]
  },
  textBlasts: {
    title: 'Text Blasts',
    icon: '📢',
    steps: [
      { title: 'Campaigns', desc: 'Create bulk messaging campaigns to send the same message to many contacts at once.' },
      { title: 'Recipient Lists', desc: 'Build and manage lists of recipients. Import from CSV or select from existing contacts.' },
      { title: 'Scheduling', desc: 'Send immediately or schedule blasts for a future date and time.' },
      { title: 'Throttling', desc: 'Blasts automatically throttle to stay within your 10DLC send limits.' },
      { title: 'Tracking', desc: 'Monitor delivery status, opt-outs, and response rates for each campaign.' }
    ]
  },
  autoResponders: {
    title: 'Auto-Reply',
    icon: '🤖',
    steps: [
      { title: 'Status-Based Replies', desc: 'Set automatic replies based on your availability status (Away, Busy, Offline).' },
      { title: 'Custom Messages', desc: 'Customize the auto-reply message for each status to set expectations with customers.' },
      { title: 'Business Hours', desc: 'Configure auto-replies for outside business hours.' }
    ]
  },
  reports: {
    title: 'Reports',
    icon: '📈',
    steps: [
      { title: 'Scheduled Reports', desc: 'Set up automated email reports delivered on a schedule (daily, weekly, monthly).' },
      { title: 'Report Types', desc: 'Choose from message volume, response time, user activity, and conversation analytics reports.' },
      { title: 'Timezone', desc: 'Reports use your configured timezone for accurate time-based data.' },
      { title: 'Export', desc: 'Download report data as CSV for further analysis.' }
    ]
  },
  billing: {
    title: 'Billing & Usage',
    icon: '💰',
    steps: [
      { title: 'Usage Summary', desc: 'View total messages sent/received, broken down by SMS, MMS, inbound, and outbound.' },
      { title: 'Per-User Usage', desc: 'See message counts per user to track team productivity.' },
      { title: 'AI Token Usage', desc: 'Monitor AI analysis token consumption and costs by provider and model.' },
      { title: 'Usage History', desc: 'Usage is tracked permanently — deleting messages does not affect billing records.' },
      { title: 'Export', desc: 'Export detailed billing data as CSV for invoicing.' }
    ]
  },
  settings: {
    title: 'Settings',
    icon: '🔧',
    steps: [
      { title: 'Profile', desc: 'Update your name, email, and password.' },
      { title: 'Two-Factor Authentication', desc: 'Enable 2FA for additional account security.' },
      { title: 'Notifications', desc: 'Configure how you receive notifications for new messages.' },
      { title: 'Auto-Reply', desc: 'Set up automatic responses for when you\'re away, busy, or offline.' }
    ]
  },
  shortcuts: {
    title: 'Keyboard Shortcuts',
    icon: '⌨️',
    steps: [
      { title: 'Enter', desc: 'Send message (in message composer)' },
      { title: 'Shift + Enter', desc: 'New line in message composer' },
      { title: '/ (slash)', desc: 'Search and insert templates while composing' },
      { title: 'Escape', desc: 'Close modal or dropdown' }
    ]
  }
};

const HelpGuide = ({ isOpen, onClose }) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setSelectedGuide(null);
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const availableGuides = Object.entries(GUIDES).filter(([_, g]) => {
    if (g.admin && !isAdmin && !isSuperAdmin) return false;
    return true;
  });

  const guide = selectedGuide ? GUIDES[selectedGuide] : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750">
          <div className="flex items-center gap-3">
            {selectedGuide && (
              <button onClick={() => { setSelectedGuide(null); setCurrentStep(0); }}
                className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {guide ? `${guide.icon} ${guide.title}` : 'Help Guide'}
              </h2>
              {!guide && <p className="text-xs text-gray-500">Select a topic to learn more</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {!guide ? (
            /* Topic Grid */
            <div className="p-5 grid grid-cols-2 gap-3">
              {availableGuides.map(([key, g]) => (
                <button key={key} onClick={() => setSelectedGuide(key)}
                  className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors text-left group">
                  <span className="text-2xl">{g.icon}</span>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-blue-700">{g.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{g.steps.length} tips</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            /* Guide Steps */
            <div className="p-5">
              {/* Step indicator */}
              <div className="flex items-center gap-1 mb-4">
                {guide.steps.map((_, i) => (
                  <button key={i} onClick={() => setCurrentStep(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === currentStep ? 'bg-blue-600 w-8' : i < currentStep ? 'bg-blue-300 w-4' : 'bg-gray-300 w-4'
                    }`} />
                ))}
                <span className="ml-auto text-xs text-gray-400">{currentStep + 1} / {guide.steps.length}</span>
              </div>

              {/* Current step */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-700/50 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {guide.steps[currentStep].title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {guide.steps[currentStep].desc}
                </p>
              </div>

              {/* All steps list */}
              <div className="mt-4 space-y-2">
                {guide.steps.map((step, i) => (
                  i !== currentStep && (
                    <button key={i} onClick={() => setCurrentStep(i)}
                      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          i < currentStep ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
                        }`}>{i + 1}</div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{step.title}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 ml-7 line-clamp-1">{step.desc}</p>
                    </button>
                  )
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        {guide && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800">
            <button onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed">
              Previous
            </button>
            <button onClick={() => {
              if (currentStep < guide.steps.length - 1) {
                setCurrentStep(currentStep + 1);
              } else {
                setSelectedGuide(null);
                setCurrentStep(0);
              }
            }}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              {currentStep < guide.steps.length - 1 ? 'Next' : 'Back to Topics'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpGuide;
